import { Skeleton } from "antd";
import { Category, ContentTopic } from "api/config/chalice-types";
import { CategorySection } from "./CategorySection";

interface CategoryListProps {
  isLoadingCategories: boolean;
  sortedCategoryNames: string[];
  topicsByCategory: Record<string, ContentTopic[]>;
  categories: Category[];
  isBulkEditMode: boolean;
  canEditBusiness: boolean;
  canGeneratePosts: boolean;
  handleTopicListItemAction: (key: string, topic: ContentTopic) => void;
  handleCategorySectionAction: (action: string, categoryName: string) => void;
  openNewTopicModal: (categoryId: string | null) => void;
  activeCategoryKeys: string[];
  handleCollapseChange: (keys: string | string[]) => void;
  selectedTopicKeys: Set<string>;
  handleTopicSelect: (topicId: string) => void;
  generateTopicKey: (topic: ContentTopic) => string;
}

export const CategoryList = ({
  isLoadingCategories,
  sortedCategoryNames,
  topicsByCategory,
  categories,
  isBulkEditMode,
  canEditBusiness,
  canGeneratePosts,
  handleTopicListItemAction,
  handleCategorySectionAction,
  openNewTopicModal,
  activeCategoryKeys,
  handleCollapseChange,
  selectedTopicKeys,
  handleTopicSelect,
  generateTopicKey,
}: CategoryListProps) => {
  if (isLoadingCategories) {
    return (
      <>
        <Skeleton active paragraph={{ rows: 2 }} title={{ width: "30%" }} />
        <Skeleton active paragraph={{ rows: 3 }} title={{ width: "40%" }} />
        <Skeleton active paragraph={{ rows: 1 }} title={{ width: "25%" }} />
      </>
    );
  }

  return sortedCategoryNames.map((categoryName) => {
    const catTopics = topicsByCategory[categoryName] || [];

    // Hide empty Uncategorized section if other categories exist and not in bulk mode
    if (
      categoryName === "Uncategorized" &&
      catTopics.length === 0 &&
      categories.length > 0 &&
      !isBulkEditMode
    ) {
      return null;
    }

    const category = categories.find((c) => c.name === categoryName);
    const categoryIdForDnd = category?.id ?? "Uncategorized"; // Use ID if exists, else 'Uncategorized'

    // Ensure we have a valid category ID or it's the Uncategorized section
    if (!category && categoryName !== "Uncategorized") {
      console.warn(`Category "${categoryName}" not found in categories array.`);
      return null; // Skip rendering if category details are missing
    }

    const enabledCount = catTopics.filter((t) => t.enabled).length;
    const totalCount = catTopics.length;

    return (
      <CategorySection
        key={categoryIdForDnd} // Use consistent keying
        category={categoryName}
        categoryIdForDnd={categoryIdForDnd}
        topics={catTopics}
        enabledCount={enabledCount}
        totalCount={totalCount}
        canEditBusiness={canEditBusiness}
        canGeneratePosts={canGeneratePosts}
        categoryColor={category?.color ?? undefined}
        onTopicAction={handleTopicListItemAction}
        onCategoryAction={handleCategorySectionAction}
        onAddTopic={() =>
          openNewTopicModal(
            categoryIdForDnd === "Uncategorized" ? null : categoryIdForDnd
          )
        }
        activeKey={activeCategoryKeys}
        onChange={handleCollapseChange}
        isBulkEditMode={isBulkEditMode}
        selectedTopicKeys={selectedTopicKeys}
        onTopicSelect={handleTopicSelect}
        generateTopicKey={generateTopicKey}
      />
    );
  });
};
